import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("admin@2rightfeet.com");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); nav("/admin"); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] grid place-items-center p-6">
      <form onSubmit={submit} className="card-surface p-10 w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-red-600 grid place-items-center font-display">2R</div>
          <div className="font-display text-xl tracking-wider">ADMIN</div>
        </div>
        <h1 className="font-display text-4xl uppercase mb-6">Sign in</h1>
        <input type="text" placeholder="Username (e.g. shubhamr@2rightfeet.com)" value={email} onChange={e=>setEmail(e.target.value)} className="w-full bg-black border border-white/10 px-4 py-3 mb-3 focus:border-red-600 outline-none" data-testid="admin-email" required />
        <div className="relative mb-4">
          <input
            type={showPw ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={e=>setPassword(e.target.value)}
            className="w-full bg-black border border-white/10 pl-4 pr-12 py-3 focus:border-red-600 outline-none"
            data-testid="admin-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPw(v => !v)}
            aria-label={showPw ? "Hide password" : "Show password"}
            title={showPw ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-red-500 p-1"
            data-testid="admin-password-toggle"
          >
            {showPw ? <EyeOff size={18}/> : <Eye size={18}/>}
          </button>
        </div>
        <button type="submit" disabled={loading} className="btn-red w-full disabled:opacity-50" data-testid="admin-login-submit">{loading ? "Signing in…" : "Sign in"}</button>
        <button
          type="button"
          onClick={() => setForgotOpen(true)}
          className="mt-3 w-full text-xs text-zinc-500 hover:text-red-500 uppercase tracking-widest text-center"
          data-testid="admin-forgot-link"
        >
          Forgot password?
        </button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-red-500 uppercase tracking-widest" data-testid="admin-login-back">
          <ArrowLeft size={12}/>Back to site
        </Link>
      </form>
      {forgotOpen && <ForgotPasswordModal defaultEmail={email} onClose={() => setForgotOpen(false)}/>}
    </div>
  );
}

function ForgotPasswordModal({ defaultEmail, onClose }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await (await import("@/lib/auth")).api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset link");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={onClose}>
      <div className="card-surface p-8 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="forgot-modal">
        <h2 className="font-display text-3xl uppercase mb-4">Forgot password</h2>
        {sent ? (
          <div className="text-zinc-300 text-sm space-y-4">
            <p>If an account exists for <strong>{email}</strong>, we've sent a reset link to your backup (recovery) email — or your login email if no backup is set.</p>
            <p className="text-zinc-500 text-xs">The link expires in 24 hours. Check spam if you don't see it in a few minutes.</p>
            <button onClick={onClose} className="btn-red w-full" data-testid="forgot-close">Got it</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p className="text-zinc-400 text-sm mb-4">Enter your admin username (the `first@2rightfeet.com` handle you were given). We'll send a reset link to the <strong>real email</strong> you saved under My Account → Recovery email. If you haven't set one yet, ask a super admin to add it.</p>
            <input
              type="text" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Username (e.g. shubhamr@2rightfeet.com)" required
              className="w-full bg-black border border-white/10 px-4 py-3 mb-4 focus:border-red-600 outline-none"
              data-testid="forgot-email"
            />
            <button type="submit" disabled={sending || !email.trim()} className="btn-red w-full disabled:opacity-50" data-testid="forgot-submit">
              {sending ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
